import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store";
import RequestDetailedRolesTab from "./request-detailed-roles-tab";
import {
    calcRequestReport,
    fetchRequestDetailed,
    getRequestStages,
    moveToApproveStep,
    moveToRejectStep,
    saveRequest,
    saveRequestComment,
    setError,
    setReportPageNumber,
    setWarning,
} from "../../../../reducers/administration/requestSlice";
import RequestDetailedActions from "./request-detailed-actions";
import RequestDetailedHeader from "./request-detailed-header";
import { CommonDialog, CommonDialogContent, CommonDialogTitle, ErrorsDialog, ProgressDialog } from "../../../common/dialogs";
import { TextInput } from "../../../common/form-controls";
import { FormattedMessage, useIntl } from "react-intl";
import { Box } from "@mui/material";
import { Button } from "../../../common/buttons";
import { ScrollableTabs } from "../../../common/tabs";

import RequestDetailedHistoryTab from "./request-detailed-history-tab";
import RequestDetailedCommentsTab from "./request-detailed-comments-tab";
import { getVisibleEntities } from "../../../../utils/formViews";
import useRequestDetailedHistoryTable from "./useRequestDetailedHistoryTable";
import ApproveRequestWithConflictsDialog from "./approve-request-with-conflicts-dialog";
import RequestDetailedConflictsTab from "./request-detailed-conflicts-tab";
import { requestDetailedTabs } from "../../../../utils/brm-utils";
import "./styles.less";
import { TableFilterData } from "../../../../types/common/table/table-types";
import PartialApproveWarningDialog from "./partial-approve-warning-dialog";

type ActionCommentDialogProps = {
    open: boolean;
    onClose: () => void;
    comment: string;
    setComment: (value: string) => void;
    onSubmit: () => void;
    requestId: string;
};

const ActionCommentDialog: React.FC<ActionCommentDialogProps> = ({ open, onClose, comment, setComment, onSubmit, requestId }) => {
    const intl = useIntl();
    const title = intl.formatMessage({ id: "request-detailed.action-comment-dialog.title" }, { requestId: requestId });

    return (
        <CommonDialog open={open} onClose={onClose}>
            <CommonDialogTitle title={title} onClose={onClose} withoutCloseIcon={false} />
            <CommonDialogContent>
                <TextInput multiline rows={5} value={comment} onChange={(event) => setComment(event.target.value)} />
                <Box gap={2} sx={{ marginTop: "15px" }} justifyContent="flex-end" display="flex">
                    <Button
                        disabled={false}
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            color: "var(--font-4)",
                            borderColor: "var(--font-4)",
                        }}
                    >
                        <FormattedMessage id="common.btn-cancel" />
                    </Button>
                    <Button
                        disabled={!(comment.length > 0)}
                        variant="contained"
                        sx={{
                            color: "var(--bg-2)",
                            backgroundColor: "var(--font-2)",
                            "&:hover": {
                                backgroundColor: "var(--font-2)",
                            },
                        }}
                        onClick={onSubmit}
                    >
                        <FormattedMessage id="common.btn-save" />
                    </Button>
                </Box>
            </CommonDialogContent>
        </CommonDialog>
    );
};

const RequestDetailedPage: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { requestId, source } = useParams();
    const [actionCommentDialogOpen, setActionCommentDialogOpen] = useState(false);
    const [approveWithConflictsMessage, setApproveWithConflictsMessage] = useState("");
    const [actionComment, setActionComment] = useState("");
    const [newRequestComment, setNewRequestComment] = useState("");
    const dispatch = useDispatch<AppDispatch>();
    const currentRequest = useSelector((state: RootState) => state.administration.requests.currentRequest);
    const visibleHistory = getVisibleEntities(currentRequest?.history ?? []);
    const requestHistoryTable = useRequestDetailedHistoryTable(visibleHistory);
    const error = useSelector((state: RootState) => state.administration.requests.error);
    const warning = useSelector((state: RootState) => state.administration.requests.warning);
    const busy = useSelector((state: RootState) => state.administration.requests.busy);
    const busyType = useSelector((state: RootState) => state.administration.requests.busyType);
    const reportReady = useSelector((state: RootState) => state.administration.requests.currentRequest?.reportReady);
    const [rolesTabResetCounter, setRolesTabResetCounter] = useState(0);

    const handleBackClick = () => {
        navigate(`/brm/${source}`);
    };

    useEffect(() => {
        dispatch(getRequestStages());
    }, [dispatch]);

    const handleSaveAndContinue = async () => {
        const resultAction = await dispatch(saveRequest());
        if (saveRequest.fulfilled.match(resultAction) && requestId) {
            navigate("testing-preparation");
        }
    };

    const handleSave = async () => {
        const resultAction = await dispatch(saveRequest());
        if (saveRequest.fulfilled.match(resultAction) && requestId) {
            dispatch(fetchRequestDetailed({ requestId: requestId }));
            setActiveTabId(requestDetailedTabs.ROLES);
            setRolesTabResetCounter((prev) => prev + 1);
        }
    };

    const handleApprove = async () => {
        if (requestId) {
            setActiveTabId(requestDetailedTabs.ROLES);
            await dispatch(moveToApproveStep({ requestId, actionComment: "", riskCheck: true })).unwrap();
            dispatch(fetchRequestDetailed({ requestId: requestId }));
        }
    };

    const handleApproveWithConflicts = async () => {
        if (requestId) {
            await dispatch(moveToApproveStep({ requestId, riskCheck: false, actionComment: approveWithConflictsMessage })).unwrap();
            setApproveWithConflictsMessage("")
            dispatch(setWarning(null));
            dispatch(fetchRequestDetailed({ requestId: requestId }));
        }
    };

    const handleReject = () => {
        setActionCommentDialogOpen(true);
    };

    const handleRejectSubmit = async () => {
        if (!requestId) return;
        await dispatch(moveToRejectStep({ requestId, actionComment }))
            .unwrap()
            .then(() => dispatch(fetchRequestDetailed({ requestId: requestId })))
            .finally(() => setActionCommentDialogOpen(false));
    };
    const handleSaveComment = () => {
        dispatch(saveRequestComment({ comment: newRequestComment }));
        setNewRequestComment("");
    };

    const tabs = [
        { id: requestDetailedTabs.ROLES, label: intl.formatMessage({ id: "request-detailed-tabs.roles-tab" }) },
        { id: requestDetailedTabs.COMMENTS, label: intl.formatMessage({ id: "request-detailed-tabs.comments-tab" }) },
        { id: requestDetailedTabs.HISTORY, label: intl.formatMessage({ id: "request-detailed-tabs.history-tab" }) },
    ];

    if (currentRequest?.hasRisks || currentRequest?.reportReady) {
        tabs.push({ id: requestDetailedTabs.CONFLICTS, label: intl.formatMessage({ id: "request-detailed-tabs.conflicts-tab" }) });
    }

    const [activeTabId, setActiveTabId] = useState(requestDetailedTabs.ROLES);

    const renderTabContent = () => {
        switch (activeTabId) {
            case requestDetailedTabs.ROLES:
                return <RequestDetailedRolesTab resetTabSignal={rolesTabResetCounter} />;
            case requestDetailedTabs.COMMENTS:
                return currentRequest?.comments ? (
                    <RequestDetailedCommentsTab
                        onSaveCommentClick={handleSaveComment}
                        newRequestComment={newRequestComment}
                        setNewRequestComment={setNewRequestComment}
                        comments={currentRequest.comments}
                        disabled={currentRequest?.readMode || !currentRequest?.currentRequestEditable}
                    />
                ) : null;
            case requestDetailedTabs.HISTORY:
                return currentRequest?.history ? (
                    <RequestDetailedHistoryTab
                        filterData={requestHistoryTable.filterData as TableFilterData[]}
                        getDialogFilters={
                            requestHistoryTable.getDialogFilters as unknown as (params: {
                                field: string;
                                search?: string;
                            }) => Promise<{ rows: { key: string; text: string }[]; total: number }>
                        }
                        setFilterData={requestHistoryTable.setFilterData}
                        requestHistory={requestHistoryTable.rows}
                        total={requestHistoryTable.pagination.total}
                        limitNumber={requestHistoryTable.pagination.rowsPerPage}
                        setPageNumber={requestHistoryTable.pagination.onSetPage}
                        pageNumber={requestHistoryTable.pagination.page}
                        setLimitNumber={requestHistoryTable.pagination.onSetRowsPerPage}
                    />
                ) : null;
            case requestDetailedTabs.CONFLICTS:
                return <RequestDetailedConflictsTab />;
            default:
                return null;
        }
    };

    return (
        <>
            <RequestDetailedHeader onBackClick={handleBackClick} />
            <ScrollableTabs
                className="request-detailed-tabs"
                tabs={tabs}
                value={activeTabId}
                onChange={async (tabId) => {
                    if (tabId === requestDetailedTabs.CONFLICTS && !reportReady && requestId) {
                        try {
                            await dispatch(saveRequest()).unwrap();
                        } catch {
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                        }
                        await dispatch(calcRequestReport(requestId)).unwrap();
                        dispatch(setReportPageNumber(1));
                    }
                    setActiveTabId(tabId);
                }}
            />
            {renderTabContent()}
            <RequestDetailedActions
                currentStage={currentRequest?.currentStage ?? ""}
                onApproveClick={handleApprove}
                onCancelProcessClick={() => {}}
                onProcessClick={() => {}}
                onRejectClick={handleReject}
                onSaveAndContinueClick={handleSaveAndContinue}
                onSaveClick={handleSave}
                disabled={(currentRequest?.readMode || !currentRequest?.currentRequestEditable) ?? false}
            />
            <ActionCommentDialog
                requestId={requestId ?? ""}
                open={actionCommentDialogOpen}
                onClose={() => setActionCommentDialogOpen(false)}
                comment={actionComment}
                setComment={setActionComment}
                onSubmit={handleRejectSubmit}
            />
            <ApproveRequestWithConflictsDialog
                onSave={handleApproveWithConflicts}
                approveMessage={approveWithConflictsMessage}
                setApproveMessage={setApproveWithConflictsMessage}
                open={warning?.type === "warning" && warning?.code === "HAS_RISKS"}
                onClose={() => dispatch(setWarning(null))}
            />
            <ProgressDialog open={busy} busyType={busyType} title="" progress={""} />
            <PartialApproveWarningDialog 
                error={warning} 
                open={warning?.type === "warning" && warning.code === "partial_approve"} 
                onClose={() => dispatch(setWarning(null))} />
            <ErrorsDialog error={error} open={error?.type === "error" && error.code != "HAS RISKS"} onClose={() => dispatch(setError(null))} />
        </>
    );
};
export default RequestDetailedPage;
